import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, Collapse, Stack, Typography, alpha, useTheme,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

/**
 * ListingToolbar - the unified "command bar" above every listings
 * table. By default the filter inputs are HIDDEN: the bar shows only
 * a "Filters" toggle (with an active-count badge), an optional
 * trailing summary, and the page's primary action(s) on the right.
 *
 * Click the toggle and the filter panel expands below. This pattern
 * keeps the listing visually quiet on first load - the user sees
 * the data, not a wall of dropdowns - while still putting power
 * users one click from full filtering.
 *
 * Props:
 *   filters         - node: filter inputs rendered inside the
 *                     collapsible panel
 *   actions         - node: always-visible right-aligned action(s)
 *   trailing        - optional summary node ("12 of 84") shown left
 *                     of actions
 *   activeCount     - integer; when > 0 the toggle shows a badge,
 *                     the panel is forced open on first interaction,
 *                     and a "Clear filters" button appears in the
 *                     panel footer
 *   onClearFilters  - called when the user hits "Clear filters"
 *   defaultOpen     - default false; if true the panel starts open
 */
const ListingToolbar = ({
  filters,
  actions,
  trailing,
  activeCount = 0,
  onClearFilters,
  defaultOpen = false,
  sx,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  const hasFilters = !!filters;

  // Auto-expand when filters become active from outside (e.g. user
  // navigates back to a page with persisted filter state, or another
  // control toggles a filter chip).
  useEffect(() => {
    if (activeCount > 0 && !open) setOpen(true);
    // We only want to re-evaluate when activeCount transitions from
    // 0 to >0. Don't re-collapse if the user manually expanded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCount > 0]);

  const handleClear = () => {
    if (onClearFilters) onClearFilters();
  };

  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.background.default,
          theme.palette.mode === 'dark' ? 0.4 : 0.6),
        overflow: 'hidden',
        ...sx,
      }}
    >
      {/* Header row - wraps on phones so actions never push off-screen. */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ p: 1, pl: 1.25, flexWrap: { xs: 'wrap', md: 'nowrap' }, rowGap: 1 }}
      >
        {hasFilters ? (
          <Button
            size="small"
            startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
            endIcon={open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
            onClick={() => setOpen((o) => !o)}
            sx={{
              textTransform: 'none',
              color: open
                ? theme.palette.primary.main
                : theme.palette.text.primary,
              fontWeight: 600,
              fontSize: 13,
              px: 1.25,
              py: 0.5,
              minHeight: 32,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.06),
              },
            }}
          >
            Filters
            {activeCount > 0 && (
              <Chip
                label={activeCount}
                size="small"
                color="primary"
                sx={{
                  ml: 0.75,
                  height: 18,
                  minWidth: 18,
                  fontSize: 10,
                  fontWeight: 700,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
          </Button>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}

        <Box sx={{ flex: 1 }} />

        {trailing && (
          <Typography variant="caption" sx={{
            color: theme.palette.text.secondary,
            fontSize: 12,
            fontWeight: 500,
          }}>
            {trailing}
          </Typography>
        )}

        {actions && (
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            {actions}
          </Box>
        )}
      </Stack>

      {/* Collapsible filter panel */}
      {hasFilters && (
        <Collapse in={open} timeout={200}>
          <Box sx={{
            px: 1.5,
            pt: 0.5,
            pb: 1.25,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.25}
              alignItems={{ xs: 'stretch', md: 'center' }}
              flexWrap="wrap"
              useFlexGap
              sx={{ width: '100%', mt: 1 }}
            >
              {filters}
            </Stack>

            {activeCount > 0 && onClearFilters && (
              <Box sx={{ mt: 1.25, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  onClick={handleClear}
                  startIcon={<FilterAltOffIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    textTransform: 'none',
                    fontSize: 12,
                    color: theme.palette.text.secondary,
                    '&:hover': { color: theme.palette.error.main, bgcolor: 'transparent' },
                  }}
                >
                  Clear filters
                </Button>
              </Box>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default ListingToolbar;
